// ============================================
// Signature Section
// Digital signature capture - Member + Witness (optional)
// Supports: Draw (mouse/touch), Text, Upload
// ============================================

import React, { useRef, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { FormSubmission, SignatureType } from '../../types';
import FormField from '../ui/FormField';
import { useLanguage } from '../../i18n/LanguageContext';

const SignatureSection: React.FC<{ onGenerate?: () => void; isGenerating?: boolean }> = ({ onGenerate, isGenerating = false }) => {
  const { t } = useLanguage();
  const { register, setValue, watch, formState: { errors } } = useFormContext<FormSubmission>();
  const signatureRef = useRef<SignatureCanvas>(null);
  const witnessSignatureRef = useRef<SignatureCanvas>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const witnessFileInputRef = useRef<HTMLInputElement>(null);

  const signatureData = watch('signature.imageData');
  const signatureType = watch('signature.signatureType') || 'draw';
  const witnessName = watch('roi.witnessName');
  const witnessSignatureData = watch('roi.witnessSignature');

  const [witnessSignatureType, setWitnessSignatureType] = useState<SignatureType>('draw');
  const [textSignature, setTextSignature] = useState('');
  const [witnessTextSignature, setWitnessTextSignature] = useState('');
  const [isDraggingSignature, setIsDraggingSignature] = useState(false);
  const [isDraggingWitness, setIsDraggingWitness] = useState(false);

  // Set today's date as default
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setValue('signature.signedDate', today);
    setValue('signature.signatureType', 'draw');
  }, [setValue]);
  // ... (rest of functions)


  // Convert text to signature image
  const generateTextSignature = (text: string, isWitness: boolean = false) => {
    if (!text.trim()) return;

    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // Completely transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'black';
      ctx.font = 'italic 72px "Brush Script MT", cursive, sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 20, 100);

      const dataUrl = canvas.toDataURL('image/png');
      if (isWitness) {
        setValue('roi.witnessSignature', dataUrl);
      } else {
        setValue('signature.imageData', dataUrl);
      }
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, isWitness: boolean = false) => {
    const file = event.target.files?.[0];
    if (!file) return;

    processFile(file, isWitness);
  };

  // Process uploaded or dropped file
  const processFile = (file: File, isWitness: boolean = false) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (isWitness) {
        setValue('roi.witnessSignature', dataUrl);
      } else {
        setValue('signature.imageData', dataUrl);
      }
    };
    reader.readAsDataURL(file);
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, isWitness: boolean = false) => {
    e.preventDefault();
    e.stopPropagation();
    if (isWitness) {
      setIsDraggingWitness(true);
    } else {
      setIsDraggingSignature(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>, isWitness: boolean = false) => {
    e.preventDefault();
    e.stopPropagation();
    if (isWitness) {
      setIsDraggingWitness(false);
    } else {
      setIsDraggingSignature(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, isWitness: boolean = false) => {
    e.preventDefault();
    e.stopPropagation();
    if (isWitness) {
      setIsDraggingWitness(false);
    } else {
      setIsDraggingSignature(false);
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0], isWitness);
    }
  };

  const handleClearSignature = () => {
    if (signatureType === 'draw' && signatureRef.current) {
      signatureRef.current.clear();
    }
    if (signatureType === 'text') {
      setTextSignature('');
    }
    if (signatureType === 'upload' && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setValue('signature.imageData', '');
  };

  const handleEndSignature = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const dataUrl = signatureRef.current.toDataURL('image/png');
      setValue('signature.imageData', dataUrl);
    }
  };

  const handleClearWitnessSignature = () => {
    if (witnessSignatureType === 'draw' && witnessSignatureRef.current) {
      witnessSignatureRef.current.clear();
    }
    if (witnessSignatureType === 'text') {
      setWitnessTextSignature('');
    }
    if (witnessSignatureType === 'upload' && witnessFileInputRef.current) {
      witnessFileInputRef.current.value = '';
    }
    setValue('roi.witnessSignature', '');
  };

  const handleEndWitnessSignature = () => {
    if (witnessSignatureRef.current && !witnessSignatureRef.current.isEmpty()) {
      const dataUrl = witnessSignatureRef.current.toDataURL('image/png');
      setValue('roi.witnessSignature', dataUrl);
    }
  };

  // Handle signature type change
  const handleSignatureTypeChange = (type: SignatureType) => {
    setValue('signature.signatureType', type);
    setValue('signature.imageData', '');
    setTextSignature('');
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleWitnessSignatureTypeChange = (type: SignatureType) => {
    setWitnessSignatureType(type);
    setValue('roi.witnessSignature', '');
    setWitnessTextSignature('');
    if (witnessSignatureRef.current) {
      witnessSignatureRef.current.clear();
    }
    if (witnessFileInputRef.current) {
      witnessFileInputRef.current.value = '';
    }
  };

  // Restore signatures if already captured
  useEffect(() => {
    if (signatureData && signatureRef.current && signatureType === 'draw') {
      signatureRef.current.fromDataURL(signatureData);
    }
    if (witnessSignatureData && witnessSignatureRef.current && witnessSignatureType === 'draw') {
      witnessSignatureRef.current.fromDataURL(witnessSignatureData);
    }
  }, []);

  return (
    <div className="space-y-8">
      <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
        <h3 className="font-semibold text-blue-900 mb-2">
          {t('signature.digitalSignature')}
        </h3>
        <p className="text-sm text-blue-700">
          {t('signature.memberOnly')}
        </p>
      </div>

      <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
        <p className="text-sm text-yellow-800">
          {t('signature.staffInstruction')}
        </p>
      </div>

      {/* Signer Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label={t('signature.signedBy')}
          error={errors.signature?.signedBy?.message}
        >
          <input
            type="text"
            {...register('signature.signedBy')}
            className="form-input w-full rounded-md border-gray-300"
            placeholder={t('signature.signedByPlaceholder')}
          />
        </FormField>

        <FormField
          label={t('signature.date')}
          error={errors.signature?.signedDate?.message}
        >
          <input
            type="date"
            min="1900-01-01"
            {...register('signature.signedDate')}
            className="form-input w-full rounded-md border-gray-300"
          />
        </FormField>
      </div>

      {/* Relationship field (if signer is not the member) */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <FormField
          label={t('signature.relationshipLabel')}
          error={errors.signature?.relationship?.message}
        >
          <input
            type="text"
            {...register('signature.relationship')}
            className="form-input w-full rounded-md border-gray-300"
            placeholder={t('signature.relationshipPlaceholder')}
          />
        </FormField>
        <p className="text-xs text-gray-500 mt-1">
          {t('signature.relationshipNote')}
        </p>
      </div>

      {/* Signature Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('signature.chooseMethod')} <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => handleSignatureTypeChange('draw')}
            className={`px-4 py-2 rounded-lg border-2 transition-colors ${signatureType === 'draw'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              {t('signature.drawSignature')}
            </span>
          </button>

          <button
            type="button"
            onClick={() => handleSignatureTypeChange('text')}
            className={`px-4 py-2 rounded-lg border-2 transition-colors ${signatureType === 'text'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t('signature.typeSignature')}
            </span>
          </button>

          <button
            type="button"
            onClick={() => handleSignatureTypeChange('upload')}
            className={`px-4 py-2 rounded-lg border-2 transition-colors ${signatureType === 'upload'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('signature.uploadSignature')}
            </span>
          </button>
        </div>
      </div>

      {/* Draw Signature */}
      {signatureType === 'draw' && (
        <div>
          <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {t('signature.clickToSign')}
              </span>
              <button
                type="button"
                onClick={handleClearSignature}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t('signature.clearSignature')}
              </button>
            </div>

            <div className="relative" style={{ height: '300px' }}>
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'signature-canvas w-full h-full',
                  style: {
                    width: '100%',
                    height: '300px',
                    backgroundColor: 'transparent',
                  },
                }}
                onEnd={handleEndSignature}
                penColor="black"
                minWidth={2}
                maxWidth={4}
              />

              {/* Signature line */}
              <div
                className="absolute bottom-10 left-8 right-8 border-b border-gray-300"
                style={{ pointerEvents: 'none' }}
              />
              <span
                className="absolute bottom-2 left-8 text-xs text-gray-400"
                style={{ pointerEvents: 'none' }}
              >
                {t('signature.clickToSign')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Text Signature */}
      {signatureType === 'text' && (
        <div>
          <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white p-4">
            <label className="block text-sm text-gray-500 mb-2">
              {t('signature.typeYourName')}
            </label>
            <input
              type="text"
              value={textSignature}
              onChange={(e) => {
                setTextSignature(e.target.value);
                generateTextSignature(e.target.value);
              }}
              className="form-input w-full rounded-md border-gray-300 text-xl"
              placeholder={t('signature.typeYourName')}
            />
            {textSignature && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">{t('signature.signaturePreview')}</p>
                <p className="text-3xl italic font-serif text-gray-800" style={{ fontFamily: '"Brush Script MT", cursive' }}>
                  {textSignature}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Signature */}
      {signatureType === 'upload' && (
        <div>
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDraggingSignature
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 bg-white'
              }`}
            onDragOver={(e) => handleDragOver(e, false)}
            onDragLeave={(e) => handleDragLeave(e, false)}
            onDrop={(e) => handleDrop(e, false)}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, false)}
              className="hidden"
              id="signature-upload"
            />
            <label
              htmlFor="signature-upload"
              className="cursor-pointer flex flex-col items-center"
            >
              <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-gray-600 font-medium">{t('signature.chooseFile')}</span>
              <span className="text-sm text-gray-400 mt-1">{t('signature.dragDropFile')}</span>
            </label>
          </div>
        </div>
      )}

      {!signatureData && (
        <div className="mt-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
          <p className="text-sm font-medium text-amber-800">
            {t('signature.signatureRequired')}
          </p>
          <p className="text-sm text-amber-700 mt-1">
            {t('signature.staffInstructionBlank')}
          </p>
        </div>
      )}

      {signatureData && (
        <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded">
          <p className="text-sm font-medium text-green-800">
            {t('signature.signatureCaptured')}
          </p>
        </div>
      )}

      {/* Hidden input to store signature data for validation */}
      <input
        type="hidden"
        {...register('signature.imageData')}
      />
      {errors.signature?.imageData && (
        <p className="mt-2 text-sm text-red-600">{errors.signature.imageData.message}</p>
      )}

      {/* Preview */}
      {signatureData && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('signature.signaturePreview')}</h3>
            <button
              type="button"
              onClick={handleClearSignature}
              className="text-sm text-red-600 hover:text-red-800"
            >
              {t('signature.clearAndResign')}
            </button>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 inline-block">
            <img
              src={signatureData}
              alt="Your signature"
              className="max-h-24"
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {t('signature.signatureAppearance')}
          </p>
        </div>
      )}

      {/* Legal Acknowledgment */}
      <div className="bg-amber-50 p-6 rounded-lg border border-amber-200">
        <h4 className="font-medium text-amber-900 mb-3">{t('signature.legalAcknowledgment')}</h4>
        <div className="text-sm text-amber-800 space-y-2">
          <p>
            {t('signature.acknowledgmentText')}
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>{t('signature.acknowledgment1')}</li>
            <li>{t('signature.acknowledgment2')}</li>
            <li>{t('signature.acknowledgment3')}</li>
            <li>{t('signature.acknowledgment4')}</li>
            <li>{t('signature.acknowledgment5')}</li>
          </ul>
        </div>
      </div>

      {/* Witness Signature Section (Optional) */}
      {witnessName && witnessName.trim() !== '' && (
        <div className="border-t pt-6">
          <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('signature.witnessSignatureTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('signature.witnessSpecified')}: <strong>{witnessName}</strong>. {t('signature.witnessSignBelow')}
            </p>

            {/* Witness Signature Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('signature.witnessChooseMethod')}
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleWitnessSignatureTypeChange('draw')}
                  className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-colors ${witnessSignatureType === 'draw'
                    ? 'border-yellow-500 bg-yellow-100 text-yellow-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {t('signature.witnessDrawSignature')}
                </button>
                <button
                  type="button"
                  onClick={() => handleWitnessSignatureTypeChange('text')}
                  className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-colors ${witnessSignatureType === 'text'
                    ? 'border-yellow-500 bg-yellow-100 text-yellow-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {t('signature.witnessTypeSignature')}
                </button>
                <button
                  type="button"
                  onClick={() => handleWitnessSignatureTypeChange('upload')}
                  className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-colors ${witnessSignatureType === 'upload'
                    ? 'border-yellow-500 bg-yellow-100 text-yellow-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {t('signature.witnessUploadSignature')}
                </button>
              </div>
            </div>

            {/* Witness Draw Signature */}
            {witnessSignatureType === 'draw' && (
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {t('signature.witnessSignatureArea')}
                  </span>
                  <button
                    type="button"
                    onClick={handleClearWitnessSignature}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('signature.witnessClearSignature')}
                  </button>
                </div>

                <div className="relative" style={{ height: '250px' }}>
                  <SignatureCanvas
                    ref={witnessSignatureRef}
                    canvasProps={{
                      className: 'signature-canvas w-full h-full',
                      style: {
                        width: '100%',
                        height: '250px',
                        backgroundColor: 'transparent',
                      },
                    }}
                    onEnd={handleEndWitnessSignature}
                    penColor="black"
                    minWidth={2}
                    maxWidth={4}
                  />

                  <div
                    className="absolute bottom-8 left-8 right-8 border-b border-gray-300"
                    style={{ pointerEvents: 'none' }}
                  />
                  <span
                    className="absolute bottom-2 left-8 text-xs text-gray-400"
                    style={{ pointerEvents: 'none' }}
                  >
                    {t('signature.witnessSignatureArea')}
                  </span>
                </div>
              </div>
            )}

            {/* Witness Text Signature */}
            {witnessSignatureType === 'text' && (
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white p-4">
                <label className="block text-sm text-gray-500 mb-2">
                  {t('signature.witnessTypeYourName')}
                </label>
                <input
                  type="text"
                  value={witnessTextSignature}
                  onChange={(e) => {
                    setWitnessTextSignature(e.target.value);
                    generateTextSignature(e.target.value, true);
                  }}
                  className="form-input w-full rounded-md border-gray-300"
                  placeholder={t('signature.witnessTypeYourName')}
                />
                {witnessTextSignature && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">{t('signature.signaturePreview')}</p>
                    <p className="text-2xl italic font-serif text-gray-800" style={{ fontFamily: '"Brush Script MT", cursive' }}>
                      {witnessTextSignature}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Witness Upload Signature */}
            {witnessSignatureType === 'upload' && (
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${isDraggingWitness
                  ? 'border-yellow-500 bg-yellow-50'
                  : 'border-gray-300 bg-white'
                  }`}
                onDragOver={(e) => handleDragOver(e, true)}
                onDragLeave={(e) => handleDragLeave(e, true)}
                onDrop={(e) => handleDrop(e, true)}
              >
                <input
                  ref={witnessFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, true)}
                  className="hidden"
                  id="witness-signature-upload"
                />
                <label
                  htmlFor="witness-signature-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-sm text-gray-600 font-medium">{t('signature.chooseFile')}</span>
                  <span className="text-xs text-gray-400 mt-1">{t('signature.imageFileFormat')}</span>
                </label>
              </div>
            )}

            {witnessSignatureData && (
              <div className="mt-3">
                <p className="text-sm text-green-600 mb-2">
                  {t('signature.witnessSignatureCaptured')}
                </p>
                <div className="bg-white border border-gray-200 rounded-lg p-2 inline-block">
                  <img
                    src={witnessSignatureData}
                    alt={t('signature.witnessSignatureAlt')}
                    className="max-h-16"
                  />
                </div>
              </div>
            )}

            <input
              type="hidden"
              {...register('roi.witnessSignature')}
            />
          </div>
        </div>
      )}

      {/* Information about where signature will appear */}
      <div className="bg-blue-50 p-6 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-3">{t('signature.signatureWillAppearOn')}</h4>
        <ul className="text-sm text-blue-800 space-y-2">
          <li className="flex items-center">
            <span className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center mr-3 text-xs font-medium">1</span>
            {t('signature.ecmReleaseOfInformation')}
          </li>
          <li className="flex items-center">
            <span className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center mr-3 text-xs font-medium">2</span>
            {t('signature.housingAssessment')}
          </li>
          <li className="flex items-center">
            <span className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center mr-3 text-xs font-medium">3</span>
            {t('signature.cssConsentForm')}
          </li>
          <li className="flex items-center">
            <span className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center mr-3 text-xs font-medium">4</span>
            {t('signature.carePlan')}
          </li>
          <li className="flex items-center">
            <span className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center mr-3 text-xs font-medium">5</span>
            {t('signature.consentPartnership')}
          </li>
          <li className="flex items-center">
            <span className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center mr-3 text-xs font-medium">6</span>
            {t('signature.cyecmAssessment')}
          </li>
        </ul>
      </div>


    </div>
  );
};

export default SignatureSection;



